import {
  doesDeviceFitRoom,
  PointPlusHalf,
  uMath,
  vectorRotationDegree
} from "src/components/Planner/planner/uMath";
import { PlannerGlobals } from "src/components/Planner/planner/planner-globals";
import { getDColsNum, getDRowsNum } from "src/components/Planner/planner/serial-layout";
import {
  getDeviceById,
  getSerialById,
  getSerObj,
  isDevMergedWithSerials,
  uDev,
  whichRoomIsPointInInclOutdoorsAndUnassigned
} from "src/components/Planner/planner/uDev";
import { canBeDevMerged, getProductOfDevice } from "src/components/Planner/planner/uDev-dom";
import { getSerialConnectionForSerialId, getSerialConnectionProps } from "src/components/Planner/planner/serialConnections";
import { drawLengthLine } from "src/components/Planner/planner/planner";

/**
 * Try to align the given Point or Device with a device's axis in the same room
 * @param {Point | D3Device} inputPoint A Device or Point that we're trying to snap to a device's axis in the room
 * @param {{id: string}} product Only check devices that are made from this product
 * @param {boolean} addCenter Pass true if the inputPoint is a top left point. Leave as false if inputPoint is a center point.
 */
export function snapDeviceToNearby(inputPoint, product, addCenter = false) {
  try {
    // get the room that we're checking
    var snapRoom = whichRoomIsPointInInclOutdoorsAndUnassigned(inputPoint);

    // return false if room doesnt exist or getting a serial (for now)
    if (!snapRoom || !product ||
        inputPoint.devices && inputPoint.devices[0] && (inputPoint.devices[0].serializable || inputPoint.devices[0].qty > 1)) { return false; }

    if (product.product_name_id && canBeDevMerged(product) && !product.serializable) { return false; }

    // get all suitable matches in the room with their centers
    var roomDevsWithCenters = PlannerGlobals.devices
    .filter(function (x) {
      var prod = getProductOfDevice(x);
      return !x.serializable && x.roomId === snapRoom.id && prod && prod.id === product.id;
    })
    .filter(function (dev) { return !inputPoint.devices || dev.deviceId !== inputPoint.devices[0].deviceId; })
    .filter(function (dev) {
      const isSingleMerged = !dev.serializable && isDevMergedWithSerials(dev.product_name_id);
      return !isSingleMerged;
    })
    .map(function (dev) { return { dev: dev, center: uDev.getCenterOfDevice_NoDOM(dev.deviceId) }; });


    if (roomDevsWithCenters.length === 0) {
      return false;
    }

    var inputCenter;

    if (addCenter) {
      // input was a top left point, so we make it to be a center point
      inputCenter = { x: inputPoint.x + PlannerGlobals.DEVICE_SIZE / 2, y: inputPoint.y + PlannerGlobals.DEVICE_SIZE / 2 };
    }
    else {
      inputCenter = { x: inputPoint.x, y: inputPoint.y };
    }

    // declaring vars
    var snapTolerance = PlannerGlobals.DEVICE_SIZE * 0.75;

    var axes = ['x', 'y'];
    var otherAxisOf = { 'x': 'y', 'y': 'x' };

    var snappedAngle = 0;
    var snappedTopLeft = { x: null, y: null };
    var snappedDist = { x: null, y: null };
    var snappedDevWithCenter = { x: null, y: null };

    var measureLinePtPairs = [];

    // try snap with all suitable devs, get the device that is the closest (min. distance)
    roomDevsWithCenters.forEach(function (devWithCenter) {
      var center = devWithCenter.center;

      // calculate X and Y axis differences
      var dist = { x: Math.abs(center.x - inputCenter.x), y: Math.abs(center.y - inputCenter.y) };

      axes.forEach(function (axis) {
        var otherAxis = otherAxisOf[axis];
        // to snap, distance has to be smaller than a given amount
        if (dist[axis] <= snapTolerance) {
          // snap to axis if couldn't snap yet, or the currently found device is closer on the other axis than the previously snapped
          if (!snappedTopLeft[axis] || dist[otherAxis] < snappedDist[otherAxis]) {
            snappedTopLeft[axis] = center[axis] - PlannerGlobals.DEVICE_SIZE / 2; // top left pt of the snapped location
            snappedDist[otherAxis] = dist[otherAxis]; // distance to the dev, but on the other axis
            snappedDevWithCenter[axis] = devWithCenter; // reference to the device that we snapped to
            snappedAngle = devWithCenter.dev ? devWithCenter.dev.angle : 0;
          }
        }
      });
    });

    // if X or Y axis was snapped, continue with processing, else return false
    if (snappedTopLeft['x'] || snappedTopLeft['y']) {
      // store snapped point in "top-left" and "center" variations
      var snappedTopLeftFinal = {
        x: snappedTopLeft['x'] || inputCenter.x - PlannerGlobals.DEVICE_SIZE / 2,
        y: snappedTopLeft['y'] || inputCenter.y - PlannerGlobals.DEVICE_SIZE / 2
      };
      var snappedCenterFinal = PointPlusHalf(snappedTopLeftFinal);


      // if there is a similar dev very close to the snapped place, invalidate the snapping, return false
      if (roomDevsWithCenters.find(function (devWithCenter) {
        return uMath.pointDistance(snappedCenterFinal, devWithCenter.center) < 0.01;
      })) {
        // there was a similar dev too close to snapped pt
        return false;
      }

      // if we only snapped one axis, so it is not a "corner" placement, further processing is done
      if (!snappedTopLeft['x'] || !snappedTopLeft['y']) {
        // check if there is a similar device at the other side of the device we snapped to, at a similar distance
        // if there is, the snap should be at the same distance that they are, eg.: O-----O-----O
        axes.forEach(function (axis) {
          var otherAxis = otherAxisOf[axis];
          if (snappedTopLeft[axis]) {
            var closestCenter = snappedDevWithCenter[axis].center[otherAxis]; // center of the snapped device
            var dist = snappedCenterFinal[otherAxis] - closestCenter; // distance from the snapped device
            var otherSideCenterOtherAxis = closestCenter - dist; // other axis component of the other side of the snapped dev

            // point to check on the other side of the device that we snapped to
            var otherSidePt = { [axis]: snappedCenterFinal[axis], [otherAxis]: otherSideCenterOtherAxis };
            // check if there is a dev on the other side with a similar distance
            var similarDevAtOtherSide = roomDevsWithCenters.find(function (devWithCenter) {
              return uMath.pointDistance(otherSidePt, devWithCenter.center) < snapTolerance;
            });

            // if there is, snap to that distance on this side
            if (similarDevAtOtherSide) {
              snappedAngle = similarDevAtOtherSide.dev ? similarDevAtOtherSide.dev.angle : 0;
              // calculate the new snapped coordinate
              var snapDevAndOtherDevDist = closestCenter - similarDevAtOtherSide.center[otherAxis];
              var thisSideCenter = closestCenter + snapDevAndOtherDevDist;
              // update snapped top left and center points with result
              snappedCenterFinal[otherAxis] = thisSideCenter;
              snappedTopLeftFinal[otherAxis] = thisSideCenter - PlannerGlobals.DEVICE_SIZE / 2;
              // add dashed line between the snapped device, and the similar one at the other side
              measureLinePtPairs.push([similarDevAtOtherSide.center, snappedDevWithCenter[axis].center]);
            }
            else {
              // if no similar dev found on other side, try snapping between two devices
              otherSideCenterOtherAxis = snappedCenterFinal[otherAxis] + dist;
              otherSidePt = { [axis]: snappedCenterFinal[axis], [otherAxis]: otherSideCenterOtherAxis };

              // check similar dev at the other side of the snapped place
              similarDevAtOtherSide = roomDevsWithCenters.find(function (devWithCenter) {
                return uMath.pointDistance(otherSidePt, devWithCenter.center) < snapTolerance;
              });

              // if found, get point between them, and snap to it
              if (similarDevAtOtherSide) {
                snappedAngle = similarDevAtOtherSide.dev ? similarDevAtOtherSide.dev.angle : 0;
                // calculate the new snapped coordinate
                var thisSideCenter = (similarDevAtOtherSide.center[otherAxis] + snappedDevWithCenter[axis].center[otherAxis]) / 2;
                // update snapped top left and center points with result
                snappedCenterFinal[otherAxis] = thisSideCenter;
                snappedTopLeftFinal[otherAxis] = thisSideCenter - PlannerGlobals.DEVICE_SIZE / 2;
                // add dashed line between the snapped place, and the similar one at the other side
                measureLinePtPairs.push([snappedCenterFinal, similarDevAtOtherSide.center]);
              }
            }

            // add dashed line between currently checked device, and the snapped device
            measureLinePtPairs.push([snappedCenterFinal, snappedDevWithCenter[axis].center]);
          }
        });
      }
      else {
        // both axes are snapped so this dev is at a "corner", add two dashed lines
        measureLinePtPairs.push([snappedCenterFinal, snappedDevWithCenter['x'].center]);
        measureLinePtPairs.push([snappedCenterFinal, snappedDevWithCenter['y'].center]);
      }

      // return data if the point fits the room
      if (doesDeviceFitRoom(snapRoom.id, snappedTopLeftFinal, snappedAngle, 1)) {
        return {
          point: snappedTopLeftFinal,
          room: snapRoom,
          rotation: snappedAngle,
          measureLinePtPairs
        }
      }
    }

    var closestDist = Number.POSITIVE_INFINITY, closestDevWithCenter;

    roomDevsWithCenters.forEach(function (devWithCenter) {
      var dist = uMath.pointDistance(devWithCenter.center, inputCenter);
      if (dist < closestDist) {
        closestDist = dist;
        closestDevWithCenter = devWithCenter;
      }
    });

    var angle = vectorRotationDegree(closestDevWithCenter.center, inputCenter);
    var ptAtOtherSide = uMath.pointAtAngleDistance(closestDevWithCenter.center, angle + 180, closestDist);

    var similarDevAtOtherSide = roomDevsWithCenters.find(function (devWithCenter) {
      return uMath.pointDistance(ptAtOtherSide, devWithCenter.center) < snapTolerance;
    });


    if (similarDevAtOtherSide) {
      var snappedAngle = similarDevAtOtherSide.dev ? similarDevAtOtherSide.dev.angle : 0;

      var distBtwnSimilarAndSnapped = uMath.pointDistance(closestDevWithCenter.center, similarDevAtOtherSide.center);
      var angleBtwnSimilarAndSnapped = vectorRotationDegree(similarDevAtOtherSide.center, closestDevWithCenter.center);

      var newSnapCenter = uMath.pointAtAngleDistance(closestDevWithCenter.center, angleBtwnSimilarAndSnapped, distBtwnSimilarAndSnapped);

      measureLinePtPairs.push([closestDevWithCenter.center, similarDevAtOtherSide.center]);
      measureLinePtPairs.push([closestDevWithCenter.center, newSnapCenter]);
      var newSnapTopLeft = {
        x: newSnapCenter.x - PlannerGlobals.DEVICE_SIZE / 2,
        y: newSnapCenter.y - PlannerGlobals.DEVICE_SIZE / 2
      };

      if (doesDeviceFitRoom(snapRoom.id, newSnapTopLeft, snappedAngle, 1)) {
        return {
          point: newSnapTopLeft,
          room: snapRoom,
          rotation: snappedAngle,
          measureLinePtPairs
        }
      }
    }

    // wasn't snapped at all
    return false;
  }
  catch (e) {
    // exception occurred while trying to snap
    console.debug(e);
    return false;
  }
}

/**
 * Draws red dashed lines between the given point pairs. Used for displaying axis snap.
 * @param {[Point,Point][]} pairs
 */
export function drawSnapLines(pairs) {
  pairs.forEach(function (pair) {
    drawLengthLine(pair[0], pair[1], undefined, undefined, undefined, undefined, "red", "2 1");
  });
}

export function doesSerialFitRoom(serialId) {
  var serial = getSerialById(serialId);
  if (!serial || serial.deviceIds.length === 0) { return null; }
  var firstDev = getDeviceById(serial.deviceIds[0]);
  if (!firstDev) { return null; }

  var roomId = firstDev.roomId;
  const conn = getSerialConnectionForSerialId(serial.id);
  const d3Dev = conn ? getSerialConnectionProps(conn.id) : getSerObj(serial);

  const topLeft = { x: d3Dev.x, y: d3Dev.y };
  const angle = d3Dev.angle;
  const rows = getDRowsNum(d3Dev);
  const cols = getDColsNum(d3Dev);

  return doesDeviceFitRoom(roomId, topLeft, angle, cols, rows);
}